import { useMemoizedFn } from 'ahooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getLastElementByClassName } from '../../ChatPage/utils';
import { throttle } from 'lodash-es';
import { useLocation } from 'umi';
import type { TStreamingState } from '../../ChatPage/types/history';
type IAutoScrollLockProps = {
  className: string;
  deps?: any[];
  streamingState: TStreamingState;
};
export const useAutoReleaseScrollLock = ({
  className: containerClass,
  streamingState,
}: IAutoScrollLockProps) => {
  // 容器
  const containerRef = useRef<HTMLElement | null>(null);
  // 存储锁定位置，false表示未锁定
  const lockedPosition = useRef<number | false>(false);
  // 是否显示箭头
  const [showDownArrow, setShowDownArrow] = useState(false);
  // 首次渲染
  const firstRender = useRef(true);
  // 用户操作标记
  const userHasScrolled = useRef(false);
  const location = useLocation();
  const snapshotId = location?.query?.snapshotId || '';
  const locateMessageId = location?.query?.messageId || '';

  useEffect(() => {
    if (streamingState === 'complete') {
      // 完成时强制滚动到底部（仅在内容未完全可见时）
      requestAnimationFrame(() => {
        if (!containerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        const isFullyVisible = scrollHeight <= clientHeight;
        const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 10;
        // 这里兜底，如果流式已完成，但是并没有触发锁定的情况下，也就是底部还有空余，则强制滚动到底部，防止猜你想问这个dom被遮挡
        if (
          (isFullyVisible || (!isNearBottom && !lockedPosition.current)) &&
          !userHasScrolled.current &&
          !snapshotId &&
          !locateMessageId
        ) {
          initScroll();
        }
        lockedPosition.current = false;
      });
    }
    if (streamingState === 'pending') {
      userHasScrolled.current = false;
      lockedPosition.current = false;
    }
  }, [streamingState, snapshotId, locateMessageId]);

  useEffect(() => {
    const container = document.querySelector(containerClass);
    if (!container) return;
    containerRef.current = container as HTMLElement;
    // 添加MutationObserver监听内容变化
    const observer = new MutationObserver(() => {
      if (
        streamingState &&
        ['progressing', 'complete', 'pending'].includes(streamingState)
      ) {
        requestAnimationFrame(checkPosition);
      } else if (
        !streamingState &&
        !userHasScrolled.current &&
        !snapshotId &&
        !locateMessageId
      ) {
        requestAnimationFrame(initScroll);
      }
      // 初始化
      if (
        (!streamingState || streamingState === 'pending') &&
        firstRender.current &&
        !snapshotId &&
        !locateMessageId
      ) {
        initScroll();
        firstRender.current = false;
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });

    // 初始检测
    checkPosition();
    return () => observer.disconnect();
  }, [streamingState, snapshotId, locateMessageId]);

  useEffect(() => {
    if (!containerRef.current) return;

    // 处理滚动事件（使用节流）
    const handleScroll = throttle(() => {
      checkArrowVisibility();
    }, 300);

    // 处理滚轮事件
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY !== 0) {
        userHasScrolled.current = true;
      }
    };

    containerRef.current.addEventListener('scroll', handleScroll);
    containerRef.current.addEventListener('wheel', handleWheel);

    return () => {
      containerRef.current?.removeEventListener('scroll', handleScroll);
      containerRef.current?.removeEventListener('wheel', handleWheel);
    };
  }, [streamingState]);

  const initScroll = useMemoizedFn(() => {
    if (!containerRef.current) return;
    // 强制重置状态
    lockedPosition.current = false;
    userHasScrolled.current = false;
    const { scrollHeight, clientHeight } = containerRef.current;
    const targetPos = scrollHeight - clientHeight;
    containerRef.current.scrollTo({ top: targetPos, behavior: 'auto' });
  });

  // 位置检测逻辑
  const checkPosition = useCallback(() => {
    const target = getLastElementByClassName('dba-signle-qa');
    if (!target || !containerRef.current) return;

    // 实时检测容器尺寸变化
    const { scrollHeight, clientHeight } = containerRef.current;

    // 计算最后一条消息的精确位置
    const targetRect = target.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    const distanceFromTop = targetRect.top - containerRect.top;
    // 当流式输出进行时
    if (['progressing', 'pending'].includes(streamingState)) {
      // 动态调整锁定阈值（70px + 10px缓冲）、以及当前qa可见高度超出当前一屏，再次兜底，防止缓冲过快
      const shouldLock =
        distanceFromTop <= 70 || clientHeight - targetRect.height <= 30;
      if (shouldLock && !lockedPosition.current) {
        // 记录锁定位置时考虑容器最新高度
        lockedPosition.current = Math.max(
          containerRef.current.scrollTop,
          scrollHeight - clientHeight - 70,
        );
      } else if (!shouldLock) {
        // 自动滚动到底部当内容超出可视区域
        // 流式输出中
        if (scrollHeight > clientHeight) {
          !userHasScrolled.current && initScroll();
        }
        lockedPosition.current = false;
      }
    }

    // 强制保持滚动位置时同步检测箭头
    if (lockedPosition.current !== false && !userHasScrolled.current) {
      containerRef.current.scrollTop = lockedPosition.current;
    }
    checkArrowVisibility();
  }, [streamingState]);

  // 检测是否需要显示向下箭头（根据滚动位置和内容高度）
  const checkArrowVisibility = useMemoizedFn(() => {
    if (!containerRef.current) return;
    // 计算容器底部距离
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const bottomDistance = scrollHeight - (scrollTop + clientHeight);
    // 底部距离超过200px
    // TODO: 临时调整
    const shouldShow = bottomDistance > 50;
    setShowDownArrow(shouldShow);
  });

  return { scrollToBottom: initScroll, showDownArrow };
};
